import { type RefObject, useLayoutEffect } from 'react';

/** Share focus visibility between native dialogs and their triggers. */
export function useFamilyFocusMode(
  rootRef: RefObject<HTMLElement>,
  returnFocusRef?: RefObject<HTMLElement>,
) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Opening a dialog may focus a control for accessibility, but that is not
    // keyboard navigation. Tab/arrow navigation below restores visible focus.
    root.dataset.familyFocus = 'pointer';

    // Track the dialog too: typing in a text field can otherwise make browsers
    // restore :focus-visible to a button originally activated with the pointer.
    const pointer = () => { root.dataset.familyFocus = 'pointer'; };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Tab' || event.key.startsWith('Arrow')) root.dataset.familyFocus = 'keyboard';
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', keyboard, true);
    return () => {
      root.dataset.familyFocus = 'pointer';
      const triggerRoot = returnFocusRef?.current?.closest<HTMLElement>('[data-family-focus]');
      if (triggerRoot) triggerRoot.dataset.familyFocus = 'pointer';
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', keyboard, true);
    };
  }, [rootRef, returnFocusRef]);
}
